/**
 * AssignUnitPanel — asignar la unidad física a una reserva.
 *
 * Lo que esta pantalla tiene que dejar claro (PRODUCT_V2_SPEC §2.1, §6.1):
 *   - lo reservado es un TIPO de alojamiento;
 *   - la unidad física es otra cosa y puede no existir todavía;
 *   - solo son elegibles las unidades de ese tipo. Una Dolce Vistas no se
 *     asigna jamás a una Dolce Privè: el desplegable no las ofrece y la
 *     comprobación se repite antes de asignar.
 *
 * El reparto automático sigue siendo el de siempre; aquí se explica en una
 * frase, no se vuelca su matemática.
 */
import React, { useMemo, useState } from "react";
import { simulateAllocation } from "@/lib/pmsSelectors";
import { isUnitEligible } from "@/lib/reservationsV2Selectors";
import { SURFACE, StatusPill, QuickAction } from "@/pages/admin/pms/v2/V2Primitives";

export default function AssignUnitPanel({ view, settings, unitLabel, onAssign, onUnassign }) {
  const r = view.reservation;
  const [choice, setChoice] = useState("");
  const [reason, setReason] = useState("");
  const [why, setWhy] = useState(false);

  const alloc = useMemo(() => simulateAllocation({
    pool_id: r.pool_id,
    check_in: r.check_in,
    check_out: r.check_out,
    accommodation_revenue_eur: r.accommodation_revenue_eur || 0,
    ignore_reservation_id: r.reservation_id,
  }), [r.pool_id, r.check_in, r.check_out, r.accommodation_revenue_eur, r.reservation_id]);

  const eligible = alloc.previews;
  const recommended = alloc.recommended?.villa || null;
  const isOverride = r.allocation?.mode === "manual_override";

  const assign = (unitId, mode) => {
    // Segunda barrera: aunque la interfaz solo ofrezca unidades del tipo
    // correcto, nunca se asigna sin volver a comprobarlo.
    if (!unitId || !isUnitEligible(r, unitId, settings)) return;
    onAssign(unitId, { mode, reason: mode === "manual_override" ? (reason || "Asignación manual") : null });
    setChoice("");
    setReason("");
  };

  return (
    <div className="space-y-3" data-testid="assign-panel">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <div className={`${SURFACE} p-3`} data-testid="assign-booked">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">Reservado</div>
          <div className="text-sm text-stone-900 dark:text-stone-100 mt-0.5">
            {view.unitType?.name || "—"}
          </div>
          <div className="text-[11px] text-stone-400 mt-0.5">Tipo de alojamiento</div>
        </div>
        <div className={`${SURFACE} p-3`} data-testid="assign-current">
          <div className="text-[10px] uppercase tracking-widest text-stone-400">
            {unitLabel.singular} asignada
          </div>
          {view.assignedUnit ? (
            <>
              <div className="text-sm text-stone-900 dark:text-stone-100 mt-0.5">
                {view.assignedUnit.villa_id} · {view.assignedUnit.name}
              </div>
              <div className="mt-1 flex items-center gap-1.5">
                <StatusPill tone={isOverride ? "amber" : "stone"} dot={false}>
                  {isOverride ? "Asignación manual" : "Asignada automáticamente"}
                </StatusPill>
              </div>
            </>
          ) : (
            <div className="text-sm text-amber-700 dark:text-amber-300 mt-0.5" data-testid="assign-unassigned">
              Sin asignar
            </div>
          )}
        </div>
      </div>

      {!isOverride && view.assignedUnit && (
        <div>
          <button
            type="button"
            onClick={() => setWhy((v) => !v)}
            aria-expanded={why}
            data-testid="assign-why"
            className="min-h-[44px] text-xs text-stone-500 hover:text-stone-900 dark:hover:text-stone-100 inline-flex items-center"
          >
            ¿Por qué esta {unitLabel.singular.toLowerCase()}?
          </button>
          {why && (
            <p className="text-[11px] text-stone-500 dark:text-stone-400 max-w-xl" data-testid="assign-why-detail">
              Elegibles en esas fechas: {eligible.length > 0
                ? eligible.map((p) => p.villa.villa_id).join(", ")
                : "ninguna"}.
              {" "}Se eligió según las reglas de reparto configuradas en Ajustes, que buscan
              equilibrar ingresos y ocupación entre las unidades del mismo tipo. El detalle del
              cálculo vive en la pantalla de Reparto.
            </p>
          )}
        </div>
      )}

      {isOverride && r.allocation?.manual_override && (
        <div className={`${SURFACE} p-3 text-xs`} data-testid="assign-override-note">
          <div className="text-[10px] uppercase tracking-widest text-amber-700 dark:text-amber-300 mb-1">
            Asignación manual
          </div>
          <div className="text-stone-700 dark:text-stone-300">
            «{r.allocation.manual_override.reason}»
          </div>
          <div className="text-[11px] text-stone-400 mt-1">
            {r.allocation.manual_override.actor_name} · {String(r.allocation.manual_override.at).slice(0, 10)}
          </div>
        </div>
      )}

      <div className={`${SURFACE} p-3 space-y-2`}>
        <div className="text-[10px] uppercase tracking-widest text-stone-400">
          {view.assignedUnit ? "Reasignar" : "Asignar"}
        </div>

        {recommended && (
          <div className="flex flex-wrap items-center gap-2">
            <span className="text-xs text-stone-600 dark:text-stone-300">
              Propuesta del reparto: {recommended.villa_id} · {recommended.name}
            </span>
            <QuickAction
              label="Usar la propuesta"
              primary
              onClick={() => assign(recommended.villa_id, "automatic")}
              testId="assign-use-recommended"
            />
          </div>
        )}

        <label className="block">
          <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
            Elegir otra {unitLabel.singular.toLowerCase()}
          </span>
          <select
            value={choice}
            onChange={(e) => setChoice(e.target.value)}
            data-testid="assign-select"
            className="dv-input w-full text-sm min-h-[44px]"
          >
            <option value="">
              {eligible.length === 0 ? "Ninguna libre en esas fechas" : "Elegir…"}
            </option>
            {eligible.map((p) => (
              <option key={p.villa.villa_id} value={p.villa.villa_id}>
                {`${p.villa.villa_id} · ${p.villa.name}`}
              </option>
            ))}
          </select>
        </label>

        {choice && (
          <>
            <label className="block">
              <span className="block text-[10px] uppercase tracking-widest text-stone-400 mb-1">
                Motivo
              </span>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="Petición del huésped, operativa…"
                data-testid="assign-reason"
                className="dv-input w-full text-sm min-h-[44px]"
              />
            </label>
            <p className="text-[11px] text-amber-700 dark:text-amber-300" data-testid="assign-manual-warning">
              La asignación manual sustituye a la automática en esta reserva.
            </p>
            <QuickAction
              label="Asignar"
              primary
              onClick={() => assign(choice, "manual_override")}
              testId="assign-confirm"
            />
          </>
        )}

        {alloc.excluded.length > 0 && (
          <p className="text-[11px] text-stone-400" data-testid="assign-excluded">
            No disponibles: {[...new Set(alloc.excluded.map((e) => `${e.villa.villa_id} (${e.reason.toLowerCase()})`))].join(" · ")}.
          </p>
        )}

        {view.assignedUnit && (
          <QuickAction
            label="Liberar la unidad"
            onClick={onUnassign}
            testId="assign-release"
          />
        )}
      </div>
    </div>
  );
}
